import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {Sparkles, MapPin, Home as HomeIcon, Search, Loader2, UserCircle2, Filter, ArrowRight, Award, Lock, Eye, ShieldCheck, GraduationCap, Clock, CalendarRange, ChevronDown, ChevronUp, MessageSquare, Waves, Briefcase, Star} from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '@/components/Layout';
import Seo from '@/components/Seo';
import CandidateShield from '@/components/CandidateShield';
import MobileFloatingCTA from '@/components/MobileFloatingCTA';
import CvthequeInternalLinks from '@/components/CvthequeInternalLinks';
import {
  useAvailableCandidates,
  formatProfileLabel,
  CANDIDATE_QUALIFICATIONS,
  SPECIALTY_LABELS,
  FRENCH_REGIONS,
  MOBILITY_LABELS,
  HOUSING_LABELS,
  formatRemaining,
  formatOnline,
  formatPeriodRange,
  getPeriodStatus,
  type CandidateQualification,
  type CandidateMobility } from
'@/hooks/useAvailableCandidates';
import { useAuth } from '@/hooks/useAuth';
import { useMessaging } from '@/hooks/useMessaging';
import { useAvailabilityDepositFlow } from '@/hooks/useAvailabilityDepositFlow';
import { slugToQualification, slugToRegion, buildCvthequePath } from '@/lib/cvthequeUrl';
import { useCvthequeContentPages, buildCvthequeContentKey, type CvthequeContentPage } from '@/hooks/useCvthequeContentPages';
import { lumi } from '@/lib/lumi';

const QUAL_COLORS: Record<CandidateQualification, string> = {
  BNSSA: 'bg-red-50 text-red-700 border-red-200',
  MNS: 'bg-blue-50 text-blue-700 border-blue-200',
  'BPJEPS AAN': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  BEESAN: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  STAPS: 'bg-indigo-50 text-indigo-700 border-indigo-200'
};

/** Thème visuel par qualification : couleurs accent pour la carte (charte respectée). */
const QUAL_THEME: Record<
  CandidateQualification,
  {
    bar: string;
    soft: string;
    softText: string;
    softBorder: string;
  }> =
{
  BNSSA: {
    bar: 'bg-gradient-to-r from-red-500 via-red-600 to-rose-600',
    soft: 'bg-red-50/70',
    softText: 'text-red-700',
    softBorder: 'border-red-100'
  },
  MNS: {
    bar: 'bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700',
    soft: 'bg-blue-50/70',
    softText: 'text-blue-700',
    softBorder: 'border-blue-100'
  },
  'BPJEPS AAN': {
    bar: 'bg-gradient-to-r from-indigo-500 via-indigo-600 to-blue-700',
    soft: 'bg-indigo-50/70',
    softText: 'text-indigo-700',
    softBorder: 'border-indigo-100'
  },
  BEESAN: {
    bar: 'bg-gradient-to-r from-indigo-500 via-indigo-600 to-blue-700',
    soft: 'bg-indigo-50/70',
    softText: 'text-indigo-700',
    softBorder: 'border-indigo-100'
  },
  STAPS: {
    bar: 'bg-gradient-to-r from-indigo-500 via-indigo-600 to-blue-700',
    soft: 'bg-indigo-50/70',
    softText: 'text-indigo-700',
    softBorder: 'border-indigo-100'
  }
};

/**
 * Filtre de disponibilité unique appliqué côté client sur la liste complète :
 * - 'ALL'  : tous les profils disponibles (maintenant + prochainement)
 * - 'now'  : disponibles maintenant (mode immédiat, fenêtre 48h)
 * - 'soon' : disponibles prochainement (mode période datée)
 */
type AvailabilityFilter = 'ALL' | 'now' | 'soon';

/**
 * Ligne "détail réservé" dans l'encart premium :
 * - en clair (gras, sombre) pour les recruteurs/admin connectés ;
 * - floutée + cadenas pour les visiteurs non recruteurs, afin de
 *   créer une frustration positive et donner envie d'en voir plus.
 */
function LockedDetail({
  icon,
  label,
  value,
  unlocked,
  placeholder = 'Réservé recruteurs'






}: {icon: React.ReactNode;label: string;value?: string | null;unlocked: boolean;placeholder?: string;}) {
  const hasValue = !!(value && value.trim());
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <span className="w-7 h-7 rounded-lg bg-white/70 border border-gray-200/70 flex items-center justify-center flex-shrink-0 text-gray-400">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400 leading-tight">
          {label}
        </dt>
        <dd className="leading-tight mt-0.5">
          {unlocked ?
          <span className="text-sm font-bold text-gray-900 break-words whitespace-normal">
              {hasValue ? value : <span className="text-gray-400 font-medium">Non précisé</span>}
            </span> :

          <span
            aria-hidden="true"
            className="select-none blur-[5px] text-sm font-bold text-gray-500 break-words">

              {hasValue ? value : placeholder}
            </span>
          }
          {!unlocked && <span className="sr-only">{placeholder}</span>}
        </dd>
      </div>
    </div>);

}

/** Chip compacte pour une info publique (Région / Mobilité / Logement). */
function PublicChip({
  icon,
  label,
  value




}: {icon: React.ReactNode;label: string;value: React.ReactNode;}) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-gray-200/90 bg-white px-3 py-2.5 min-w-0 min-h-[52px] shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors">
      <span className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-200/70 flex items-center justify-center flex-shrink-0 text-gray-400">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-gray-400 leading-none">
          {label}
        </p>
        <p className="text-[13px] font-bold text-gray-900 leading-snug mt-1 break-words">
          {value}
        </p>
      </div>
    </div>);

}

export default function AvailableCandidates() {
  const { items, loading, fetchAllPublic } = useAvailableCandidates();
  const [availability, setAvailability] = useState<AvailabilityFilter>('ALL');
  const { isAuthenticated, isRecruiter, isAdmin, user, profile } = useAuth();
  const navigate = useNavigate();
  const { findOrCreateConversation } = useMessaging(user?.userId);
  const [contactingId, setContactingId] = useState<string | null>(null);

  // Flux de dépôt de disponibilité (pop-up puis inscription/connexion candidat)
  const { openModal: openAvailabilityModal, modal: availabilityModal } = useAvailabilityDepositFlow();

  const canSeeFullDetails = isAuthenticated && (isRecruiter || isAdmin);

  const handleContactCandidate = async (candidate: typeof items[0]) => {
    if (!isAuthenticated || !user?.userId) {
      toast.error('Connectez-vous en recruteur pour contacter ce candidat.');
      navigate('/inscription?role=recruiter');
      return;
    }
    if (!isRecruiter && !isAdmin) {
      toast.error('Réservé aux recruteurs.');
      return;
    }
    setContactingId(candidate._id);
    const conv = await findOrCreateConversation({
      peerUserId: candidate.creator,
      peerRole: 'candidate',
      peerName: formatProfileLabel(candidate.qualification, candidate.publicId),
      peerEmail: candidate.email,
      currentRole: (profile?.role || 'recruiter') as 'recruiter' | 'admin' as 'recruiter',
      currentName: profile?.fullName || profile?.company || user?.userName || '',
      currentEmail: user?.email,
      context: {
        type: 'cvtheque',
        availableCandidateId: candidate._id,
        candidatePublicId: candidate.publicId
      },
      subject: `Contact CVthèque · ${formatProfileLabel(candidate.qualification, candidate.publicId)}`
    });
    setContactingId(null);
    if (conv) {
      navigate(`/messages?c=${conv._id}`);
    } else {
      toast.error('Impossible d\'ouvrir la conversation.');
    }
  };

  // Paramètres d'URL pour les pages SEO indexables /disponibles/:qualif/:region
  const { qualifSlug, regionSlug } = useParams<{ qualifSlug?: string; regionSlug?: string }>();
  const routeQualif = useMemo(() => slugToQualification(qualifSlug), [qualifSlug]);
  const routeRegion = useMemo(() => slugToRegion(regionSlug), [regionSlug]);

  // Contenu SEO enrichi (méta, photo, texte) géré en admin pour les pages
  // qualification + région (collection cvtheque_content_pages), ex: /disponibles/bnssa/bretagne
  const { fetchByKey: fetchContentPageByKey } = useCvthequeContentPages();
  const [contentPage, setContentPage] = useState<CvthequeContentPage | null>(null);
  useEffect(() => {
    let cancelled = false;
    const key = buildCvthequeContentKey(qualifSlug, regionSlug);
    if (!key) {
      setContentPage(null);
      return;
    }
    fetchContentPageByKey(key).then((page) => {
      if (cancelled) return;
      setContentPage(page && page.isActive !== false ? page : null);
    });
    return () => {
      cancelled = true;
    };
  }, [qualifSlug, regionSlug, fetchContentPageByKey]);

  const [search, setSearch] = useState('');
  const [qualif, setQualif] = useState<CandidateQualification | 'ALL'>(routeQualif ?? 'ALL');
  const [mobility, setMobility] = useState<CandidateMobility | 'ALL'>('ALL');
  const [region, setRegion] = useState<string | 'ALL'>(routeRegion ?? 'ALL');

  // Resynchronise les filtres quand l'URL SEO change (navigation entre pages indexables)
  useEffect(() => {
    setQualif(routeQualif ?? 'ALL');
  }, [routeQualif]);
  useEffect(() => {
    setRegion(routeRegion ?? 'ALL');
  }, [routeRegion]);

  // Métadonnées SEO dynamiques selon la page (qualification / région)
  // Priorité au contenu géré en admin (cvtheque_content_pages) quand il existe.
  const seoMeta = useMemo(() => {
    if (contentPage && routeQualif && routeRegion) {
      return {
        title: contentPage.metaTitle,
        description: contentPage.metaDescription,
        canonical: `https://www.bnssa-mns.fr${buildCvthequePath(routeQualif, routeRegion)}`,
        h1: `${routeQualif} disponibles en ${routeRegion}`
      };
    }
    if (routeQualif && routeRegion) {
      return {
        title: `${routeQualif} disponibles en ${routeRegion} | CVthèque Emplois BNSSA & MNS`,
        description: `Trouvez des ${routeQualif} disponibles immédiatement ou prochainement en ${routeRegion}. Profils vérifiés, mobilité et logement renseignés sur Emplois BNSSA & MNS.`,
        canonical: `https://www.bnssa-mns.fr${buildCvthequePath(routeQualif, routeRegion)}`,
        h1: `${routeQualif} disponibles en ${routeRegion}`
      };
    }
    if (routeQualif) {
      return {
        title: `${routeQualif} disponibles partout en France | CVthèque Emplois BNSSA & MNS`,
        description: `Consultez les ${routeQualif} disponibles immédiatement ou pour une prochaine mission, région par région, sur la CVthèque Emplois BNSSA & MNS.`,
        canonical: `https://www.bnssa-mns.fr${buildCvthequePath(routeQualif)}`,
        h1: `${routeQualif} disponibles`
      };
    }
    return {
      title: 'CVthèque BNSSA & MNS — Ils sont disponibles ! | bnssa-mns.fr',
      description: 'Consultez les BNSSA, BPJEPS, BEESAN et STAPS disponibles immédiatement ou pour une prochaine mission sur Emplois BNSSA & MNS. Identifiant anonyme, région, mobilité, logement.',
      canonical: 'https://www.bnssa-mns.fr/disponibles',
      h1: null as string | null
    };
  }, [routeQualif, routeRegion, contentPage]);

  const [validatedAeCreators, setValidatedAeCreators] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Tick pour rafraîchir les compteurs toutes les 60s
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    fetchAllPublic();
  }, [fetchAllPublic]);

  useEffect(() => {
    let cancelled = false;
    const loadValidatedAe = async () => {
      try {
        const { list } = await lumi.entities.pro_dossiers.list({
          filter: { validationStatus: 'approved' },
          sort: { updatedAt: -1 },
          limit: 500
        });
        if (cancelled) return;
        const creators = new Set<string>();
        (list as Array<{creator?: string;}>).forEach((d) => {
          if (d?.creator) creators.add(d.creator);
        });
        setValidatedAeCreators(creators);
      } catch (e) {
        console.error('[AvailableCandidates] failed to load validated AE dossiers:', e);
      }
    };
    loadValidatedAe();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeFilterCount =
  (search.trim() ? 1 : 0) + (
  availability !== 'ALL' ? 1 : 0) + (
  qualif !== 'ALL' ? 1 : 0) + (
  region !== 'ALL' ? 1 : 0) + (
  mobility !== 'ALL' ? 1 : 0);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      const isPeriod = c.availabilityMode === 'period';
      const isNow = c.isAvailableNow === true && !isPeriod;
      if (availability === 'now' && !isNow) return false;
      if (availability === 'soon' && !isPeriod) return false;
      if (qualif !== 'ALL' && c.qualification !== qualif) return false;
      if (mobility !== 'ALL' && c.mobility !== mobility) return false;
      if (region !== 'ALL' && (c.region || '') !== region) return false;
      if (q) {
        const hay = canSeeFullDetails ?
        `${c.publicId ?? ''} ${c.qualification} ${c.city ?? ''} ${c.region ?? ''} ${c.email ?? ''} ${c.phone ?? ''}`.toLowerCase() :
        `${c.publicId ?? ''} ${c.qualification} ${c.region ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, availability, qualif, mobility, region, canSeeFullDetails]);

  return (
    <Layout>
      <Seo
        title={seoMeta.title}
        description={seoMeta.description}
        canonical={seoMeta.canonical} />


      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0b1020] via-[#0e1733] to-[#1a1030] text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-60 bg-[radial-gradient(ellipse_at_top_left,rgba(220,38,38,0.45),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(37,99,235,0.45),transparent_55%)]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            CVthèque
          </div>
          <h1
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight"
            style={{ fontFamily: "'Archivo', system-ui, sans-serif", letterSpacing: '-0.03em' }}>

            {seoMeta.h1 ?
            <span className="bg-gradient-to-r from-red-400 via-fuchsia-400 to-blue-400 bg-clip-text text-transparent">
                {seoMeta.h1}
              </span> :
            <>
                Ils sont{' '}
                <span className="bg-gradient-to-r from-red-400 via-fuchsia-400 to-blue-400 bg-clip-text text-transparent">
                  disponibles&nbsp;!
                </span>
              </>
            }
          </h1>
          {contentPage?.introHtml ?
          <div
            className="mt-4 max-w-2xl text-base sm:text-lg text-white/80 [&_strong]:text-white [&_strong]:font-bold"
            dangerouslySetInnerHTML={{ __html: contentPage.introHtml }} /> :


          <p className="mt-4 max-w-2xl text-base sm:text-lg text-white/80">
            {seoMeta.h1 ?
            <>Profils <strong>disponibles immédiatement</strong> ou <strong>pour une prochaine mission</strong>. Identifiant anonyme, mobilité et logement renseignés.</> :
            <>Consultez les BNSSA, BPJEPS, BEESAN et STAPS <strong>disponibles immédiatement</strong> ou <strong>pour une prochaine mission</strong>.</>}
          </p>
          }

          {contentPage?.heroImage &&
          <div className="mt-6 max-w-2xl rounded-2xl overflow-hidden border border-white/15 shadow-xl">
              <img
              src={contentPage.heroImage}
              alt={contentPage.metaTitle}
              className="w-full h-44 sm:h-56 object-cover"
              loading="lazy" />

            </div>
          }

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={openAvailabilityModal}
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors">

              <UserCircle2 className="w-4 h-4" />
              Je suis disponible
              <ArrowRight className="w-4 h-4" />
            </button>
            {!canSeeFullDetails &&
            <Link
              to="/inscription?role=recruiter"
              className="inline-flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-white/10 text-white px-5 py-3 rounded-xl text-sm font-semibold transition-colors">

                <Lock className="w-4 h-4" />
                Contacter les profesionnels disponibles
              </Link>
            }
            {canSeeFullDetails &&
            <span className="inline-flex items-center gap-2 border border-emerald-300/30 bg-emerald-500/10 text-emerald-100 px-5 py-3 rounded-xl text-sm font-semibold">
                <Eye className="w-4 h-4" />
                Mode recruteur — messagerie activée
              </span>
            }
          </div>
        </div>
      </section>

      {/* Bandeau confidentialité */}
      {!canSeeFullDetails &&
      <section className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-start sm:items-center gap-3 text-amber-900 text-xs sm:text-sm">
            <Lock className="w-4 h-4 flex-shrink-0 mt-0.5 sm:mt-0" />
            <p className="min-w-0">
              Les coordonnées des candidats restent privées. Aucun nom n'est
              publié — chaque profil apparaît sous un identifiant du type{' '}
              <span className="font-mono font-semibold">BNSSA-A7K9X2</span>.{' '}
              <Link
              to="/inscription?role=recruiter"
              className="underline font-semibold hover:text-amber-700">

                Connectez-vous en recruteur
              </Link>{' '}
              pour les contacter.
            </p>
          </div>
        </section>
      }

      {/* Filtre disponibilité : Tous / Maintenant / Prochainement */}
      <section className="bg-white border-b border-gray-100 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex sm:inline-flex w-full sm:w-auto items-center gap-1 p-1 rounded-xl bg-gray-100 border border-gray-200">
            <button
              type="button"
              onClick={() => setAvailability('ALL')}
              aria-pressed={availability === 'ALL'}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3.5 py-2 rounded-lg text-[11px] sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              availability === 'ALL' ?
              'bg-white text-gray-900 shadow-sm' :
              'text-gray-500 hover:text-gray-700'}`
              }>

              <UserCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              Tous
            </button>
            <button
              type="button"
              onClick={() => setAvailability('now')}
              aria-pressed={availability === 'now'}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3.5 py-2 rounded-lg text-[11px] sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              availability === 'now' ?
              'bg-white text-gray-900 shadow-sm' :
              'text-gray-500 hover:text-gray-700'}`
              }>

              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              Maintenant
            </button>
            <button
              type="button"
              onClick={() => setAvailability('soon')}
              aria-pressed={availability === 'soon'}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3.5 py-2 rounded-lg text-[11px] sm:text-sm font-semibold transition-colors whitespace-nowrap ${
              availability === 'soon' ?
              'bg-white text-gray-900 shadow-sm' :
              'text-gray-500 hover:text-gray-700'}`
              }>

              <CalendarRange className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              Prochainement
            </button>
          </div>
        </div>
      </section>

      {/* Filtres (pliables sur mobile, ouverts sur desktop) */}
      <section className="bg-white border-b border-gray-100 sticky top-[66px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-4">
          {/* Barre compacte mobile : bouton Filtres + compteur */}
          <div className="flex md:hidden items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              aria-controls="cvtheque-filters"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50">

              <Filter className="w-4 h-4" />
              Filtres
              {activeFilterCount > 0 &&
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold">
                  {activeFilterCount}
                </span>
              }
              {filtersOpen ?
              <ChevronUp className="w-4 h-4 text-gray-400" /> :

              <ChevronDown className="w-4 h-4 text-gray-400" />
              }
            </button>
          </div>

          {/* Grille de filtres : visible desktop, repliable mobile */}
          <div
            id="cvtheque-filters"
            className={`${filtersOpen ? 'grid' : 'hidden'} md:grid grid-cols-2 md:grid-cols-12 gap-2 sm:gap-3 mt-2 md:mt-0`}>

            <div className="col-span-2 md:col-span-4 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                placeholder={
                canSeeFullDetails ?
                'Identifiant, ville, région…' :
                'Identifiant (ex. BNSSA-A7K9X2), région ou qualification…'
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 sm:py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

            </div>
            <div className="col-span-1 md:col-span-3">
              <select
                value={qualif}
                onChange={(e) => setQualif(e.target.value as CandidateQualification | 'ALL')}
                className="w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

                <option value="ALL">Toutes qualifications</option>
                {CANDIDATE_QUALIFICATIONS.map((q) =>
                <option key={q} value={q}>{q}</option>
                )}
              </select>
            </div>
            <div className="col-span-1 md:col-span-3">
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

                <option value="ALL">Toutes régions</option>
                {FRENCH_REGIONS.map((r) =>
                <option key={r} value={r}>{r}</option>
                )}
              </select>
            </div>
            <div className="col-span-2 md:col-span-2">
              <select
                value={mobility}
                onChange={(e) => setMobility(e.target.value as CandidateMobility | 'ALL')}
                className="w-full px-2.5 sm:px-3 py-2 sm:py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

                <option value="ALL">Toutes mobilités</option>
                <option value="non">Non mobile</option>
                <option value="locale">Locale</option>
                <option value="departementale">Départementale</option>
                <option value="regionale">Régionale</option>
                <option value="france">Partout en France</option>
              </select>
            </div>
          </div>

        </div>
      </section>

      {/* Compteur hors sticky : gros, gras, centré */}
      <section className="bg-gray-50 pt-8 pb-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {loading ?
          <span className="text-sm text-gray-500">Chargement…</span> :

          <p
            className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 tracking-tight"
            style={{ fontFamily: "'Archivo', system-ui, sans-serif", letterSpacing: '-0.02em' }}>

              {filtered.length}{' '}
              professionnel{filtered.length > 1 ? 's' : ''} disponible{filtered.length > 1 ? 's' : ''}
              {availability === 'soon' ? ' prochainement' : availability === 'now' ? ' maintenant' : ''}
            </p>
          }
        </div>
      </section>

      {/* Liste */}
      <section className="pt-4 pb-24 md:pb-10 bg-gray-50 min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading &&
          <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Chargement de la CVthèque…</span>
            </div>
          }

          {!loading && filtered.length === 0 &&
          <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-10 text-center max-w-2xl mx-auto">
              <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-gray-900">
                Aucun candidat ne correspond à vos critères pour le moment
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                {availability === 'soon' ?
              'Aucun professionnel n\'a publi\u00e9 de disponibilit\u00e9 sur une p\u00e9riode pour le moment. Revenez bient\u00f4t ou ajustez vos filtres.' :
              'Revenez bient\u00f4t ou ajustez vos filtres pour d\u00e9couvrir les profils disponibles.'}
              </p>
              <Link
              to="/dashboard?tab=disponible"
              className="inline-flex items-center gap-2 mt-5 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800">

                Inscrire mon profil
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          }

          {!loading && filtered.length > 0 &&
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((c) => {
              const qColor =
              QUAL_COLORS[c.qualification] ?? 'bg-gray-50 text-gray-700 border-gray-200';
              const theme = QUAL_THEME[c.qualification] ?? {
                bar: 'bg-gradient-to-r from-gray-500 to-gray-700',
                soft: 'bg-gray-50',
                softText: 'text-gray-700',
                softBorder: 'border-gray-100'
              };
              const profileLabel = formatProfileLabel(c.qualification, c.publicId);
              const isPeriod = c.availabilityMode === 'period';
              const isAvailableNow = c.isAvailableNow === true;
              const isValidatedAe = validatedAeCreators.has(c.creator);
              const remaining = c.availableUntil ? formatRemaining(c.availableUntil) : '';
              const onlineLabel = formatOnline(c.availableSince);
              const periodRange = isPeriod ? formatPeriodRange(c.periodStart, c.periodEnd) : '';
              const periodStatus = isPeriod ? getPeriodStatus(c.periodStart, c.periodEnd) : 'unknown';
              return (
                <article
                  key={c._id}
                  className="group relative bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-[0_2px_10px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_50px_-12px_rgba(15,23,42,0.18)] hover:-translate-y-1 hover:border-gray-300 transition-all duration-300 flex flex-col">

                    {/* Accent bar haut coloré par qualification */}
                    <div className={`h-1.5 w-full ${theme.bar}`} aria-hidden="true" />

                    <div className="p-6 flex flex-col gap-5 flex-1">
                      {/* Strate 1 — Status banner */}
                      {isPeriod ?
                    <div className="flex flex-col items-start gap-1.5">
                          <span
                        className={`inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide rounded-full px-3 py-1.5 border ${
                        periodStatus === 'active' ?
                        'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        'bg-blue-50 text-blue-700 border-blue-200'}`
                        }>

                            <CalendarRange className="w-3.5 h-3.5" />
                            {periodStatus === 'active' ? 'Disponible sur cette période' : 'Disponible à venir'}
                          </span>
                        </div> :
                    isAvailableNow ?
                    <div className="flex flex-col items-start gap-1.5">
                          <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide rounded-full px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <span className="relative flex w-2 h-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                            </span>
                            Disponible Immédiatement !
                          </span>
                          <span className="text-[11px] font-medium text-gray-500 truncate">
                            {onlineLabel}
                          </span>
                        </div> :

                    <div className="flex items-center justify-between gap-3">
                          <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide rounded-full px-3 py-1.5 bg-gray-100 text-gray-500 border border-gray-200">
                            <span className="w-2 h-2 rounded-full bg-gray-400" />
                            Hors ligne
                          </span>
                        </div>
                    }

                      {/* Strate 2 — Hero identité */}
                      <div className="flex flex-col items-center text-center gap-3 pt-1">
                        <CandidateShield
                        qualification={c.qualification}
                        size={76}
                        variant="light"
                        verified={isValidatedAe || !!c.diplomaUrl} />

                        <div className="min-w-0 w-full">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                            Identifiant
                          </p>
                          <h3
                          className="font-mono text-xl sm:text-2xl lg:text-[26px] font-black text-gray-900 tracking-tight break-all whitespace-normal mt-1"
                          title={profileLabel}>

                            {profileLabel}
                          </h3>
                          <div className="flex flex-wrap items-center justify-center gap-1.5 mt-2">
                            <span
                            className={`inline-flex items-center gap-1 text-xs font-bold border rounded-full px-3 py-1 ${qColor}`}>

                              <Award className="w-3.5 h-3.5" />
                              {c.qualification}
                            </span>
                            {isValidatedAe ?
                          <span className="inline-flex items-center gap-1 text-xs font-bold border rounded-full px-3 py-1 bg-amber-50 text-amber-800 border-amber-200">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                Auto-Entrepreneur validé
                              </span> :
                          c.diplomaUrl ?
                          <span className="inline-flex items-center gap-1 text-xs font-bold border rounded-full px-3 py-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                                <GraduationCap className="w-3.5 h-3.5" />
                                Diplôme vérifié
                              </span> :
                          null}
                          </div>
                        </div>
                      </div>

                      {/* Strate 3 — Période datée en exergue */}
                      {isPeriod && periodRange &&
                    <div
                      className={`rounded-xl border ${theme.softBorder} ${theme.soft} px-4 py-3 flex items-center gap-3`}>

                          <CalendarRange className={`w-5 h-5 flex-shrink-0 ${theme.softText}`} />
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                              Disponibilité
                            </p>
                            <p className={`text-sm font-bold break-words ${theme.softText}`}>
                              {periodRange}
                            </p>
                          </div>
                        </div>
                    }

                      {/* Strate 3 — Compte à rebours en exergue */}
                      {!isPeriod && isAvailableNow && remaining &&
                    <div
                      className={`rounded-xl border ${theme.softBorder} ${theme.soft} px-4 py-3 flex items-center gap-3`}>

                          <Clock className={`w-5 h-5 flex-shrink-0 ${theme.softText}`} />
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                              Disponibilité
                            </p>
                            <p className={`text-sm font-bold break-words ${theme.softText}`}>
                              {remaining}
                            </p>
                          </div>
                        </div>
                    }

                      {/* Strate 4a — Infos publiques compactes en chips */}
                      <div className="grid grid-cols-2 gap-2 items-stretch">
                        <PublicChip
                        icon={<MapPin className="w-3.5 h-3.5" />}
                        label="Région"
                        value={
                        c.region ?
                        c.region :

                        <span className="text-gray-400 font-medium">Non précisée</span>

                        } />

                        <PublicChip
                        icon={<HomeIcon className="w-3.5 h-3.5" />}
                        label="Logement"
                        value={
                        c.housingNeed === 'oui' || c.housingNeed === 'oui_si_eloigne' ?
                        <>
                                Nécessaire
                                {c.housingNeed === 'oui_si_eloigne' &&
                          <span className="text-gray-500 font-medium"> (si éloigné)</span>
                          }
                              </> :

                        HOUSING_LABELS[c.housingNeed]

                        } />

                        <div className="col-span-2">
                          <PublicChip
                          icon={<ArrowRight className="w-3.5 h-3.5" />}
                          label="Mobilité"
                          value={
                          <>
                                {MOBILITY_LABELS[c.mobility]}
                                {c.mobility === 'locale' && c.mobilityRadiusKm ?
                            <span className="text-gray-500 font-medium"> — {c.mobilityRadiusKm} km</span> :
                            null}
                                {c.mobility === 'departementale' && canSeeFullDetails && c.mobilityDepartment ?
                            <span className="text-gray-500 font-medium"> — {c.mobilityDepartment}</span> :
                            null}
                                {c.mobility === 'regionale' && c.mobilityRegion ?
                            <span className="text-gray-500 font-medium"> — {c.mobilityRegion}</span> :
                            null}
                              </>
                          } />

                        </div>
                      </div>

                      {/* Strate 4b — Encart « Détails réservés recruteurs » (teaser flouté) */}
                      <dl
                      className={`relative rounded-2xl border p-4 space-y-3 ${
                      canSeeFullDetails ?
                      `${theme.softBorder} ${theme.soft}` :
                      'border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100/60'}`
                      }>

                        <div className="flex items-center justify-between gap-2">
                          <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500">
                            {canSeeFullDetails ?
                          <Eye className={`w-3.5 h-3.5 ${theme.softText}`} /> :

                          <Lock className="w-3.5 h-3.5 text-gray-400" />
                          }
                            {canSeeFullDetails ? 'Détails du profil' : 'Détails réservés recruteurs'}
                          </p>
                        </div>

                        <LockedDetail
                        icon={<MapPin className="w-3.5 h-3.5" />}
                        label="Ville"
                        value={c.city}
                        unlocked={canSeeFullDetails}
                        placeholder="Ville masquée" />


                        {c.qualification === 'BNSSA' ?
                      c.workEnvironment &&
                      <LockedDetail
                        icon={<Waves className="w-3.5 h-3.5" />}
                        label="Milieu de travail"
                        value={c.workEnvironment}
                        unlocked={canSeeFullDetails} /> :


                      c.specialty &&
                      <LockedDetail
                        icon={<Star className="w-3.5 h-3.5" />}
                        label="Spécialité"
                        value={SPECIALTY_LABELS[c.specialty] ?? c.specialty}
                        unlocked={canSeeFullDetails} />

                      }

                        {c.experience &&
                      <LockedDetail
                        icon={<Briefcase className="w-3.5 h-3.5" />}
                        label="Expérience"
                        value={c.experience}
                        unlocked={canSeeFullDetails} />

                      }

                        {!canSeeFullDetails &&
                      <p className="text-[11px] text-gray-400 pt-0.5 leading-snug">Seuls les recruteurs connectés ont accès à ces détails.

                      </p>
                      }
                      </dl>

                      {/* Strate 5 — CTA contact : messagerie interne si recruteur connecté, sinon inscription/connexion */}
                      <div className="mt-auto pt-1">
                        {canSeeFullDetails ?
                      <button
                        type="button"
                        onClick={() => handleContactCandidate(c)}
                        disabled={contactingId === c._id}
                        aria-label="Envoyer un message via la messagerie interne"
                        className="group/btn w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold text-sm px-4 py-3 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0">

                            {contactingId === c._id ?
                        <Loader2 className="w-4 h-4 animate-spin" /> :

                        <MessageSquare className="w-4 h-4" />
                        }
                            {contactingId === c._id ? 'Ouverture…' : 'Envoyer un message'}
                            {contactingId !== c._id &&
                        <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                        }
                          </button> :

                      <Link
                        to="/inscription?role=recruiter"
                        className="group/btn w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold text-sm px-4 py-3 rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 transition-all">

                            <Lock className="w-4 h-4" />
                            Contacter maintenant !
                            <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                          </Link>
                      }
                      </div>
                    </div>
                  </article>);

            })}
            </div>
          }
        </div>
      </section>

      {/* Contenu SEO enrichi (texte rédactionnel géré en admin) pour les pages qualification+région */}
      {contentPage?.bodyHtml &&
      <section className="bg-white border-t border-gray-100 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
            className={`seo-rich-content ${routeQualif === 'BNSSA' ? 'seo-accent-red' : 'seo-accent-blue'}`}
            dangerouslySetInnerHTML={{ __html: contentPage.bodyHtml }} />

          </div>
        </section>
      }

      {/* Maillage interne SEO : liens crawlables vers les pages qualification & région */}
      <CvthequeInternalLinks
        currentQualif={routeQualif}
        currentRegion={routeRegion} />


      {/* CTA flottant zone pouce — mobile only, listes longues */}
      <MobileFloatingCTA
        onClick={openAvailabilityModal}
        variant="emerald"
        hidden={loading || filtered.length < 6}
        ariaLabel="Ajouter mes disponibilités">

        <UserCircle2 className="w-5 h-5" />
        Ajouter mes disponibilités
        <ArrowRight className="w-4 h-4" />
      </MobileFloatingCTA>

      {/* Pop-up de dépôt de disponibilité (étape 1) */}
      {availabilityModal}
    </Layout>);

}